import * as THREE from "three";

/**
 * Frame 001 · Laboratory — the visual research environment.
 *
 * Not SONMI. Not the presentation. Not a UI. An isolated scene that
 * exists so the material can teach us how it wants to behave.
 *
 * Governed by:
 *   /app/memory/MATERIAL_SPECIFICATION.md
 *   /app/memory/FRAME_001_SPECIFICATION.md
 *
 * The goal is observation, not decoration.
 */


// -- Parameter defaults ----------------------------------------------------

export const DEFAULT_PARAMS = {
  // Material — the frost
  surfaceRoughness: 0.85,
  subsurfaceScattering: 1.6,     // higher = light spreads farther through material
  translucency: 1.0,             // MeshPhysicalMaterial.transmission
  opacity: 1.0,                  // MeshPhysicalMaterial.opacity
  internalDiffusion: 0.7,        // modulates transmission roughness via material.roughness
  surfaceDepth: 1.2,             // MeshPhysicalMaterial.thickness

  // Facets — latent geometry within the material
  facetDensity: 24,              // number of Voronoi cells
  facetVisibility: 0.0,          // 0 = perfectly frosted, higher = facets emerge
  facetTransparency: 0.5,        // blending factor (multiplied into visibility)
  facetScale: 2.5,               // texture repeat across the surface

  // Light — the beacon
  lightIntensity: 6.0,
  lightRadius: 0.18,             // radius of the emissive source sphere
  lightColor: "#e08a72",         // warm amber; a candle, not a laser
  lightDistance: 2.0,            // distance behind the material
  pulseAmplitude: 0.35,          // fraction of intensity modulation
  pulseFrequency: 0.14,          // Hz — slower than a resting human breath
  refractionStrength: 1.45,      // material IOR

  // Camera — patient, curious, never searching
  cameraDistance: 0.5,           // distance from the front face
  cameraDriftAmplitude: 0.09,
  cameraDriftSpeed: 0.05,
};


// -- Procedural facet normal map ------------------------------------------

/**
 * Voronoi-like facet field baked into a normal texture. facetDensity
 * controls how many cells; the resulting normal map, applied through
 * MeshPhysicalMaterial.normalMap, makes latent facets emerge from
 * the frost as facetVisibility increases.
 *
 * O(size^2 * density). Called only when facetDensity changes.
 */
function generateFacetNormalMap(density, size = 256) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "rgb(128,128,255)";
  ctx.fillRect(0, 0, size, size);

  const pts = [];
  const count = Math.max(2, Math.round(density));
  for (let i = 0; i < count; i++) {
    pts.push({ x: Math.random() * size, y: Math.random() * size });
  }

  const img = ctx.getImageData(0, 0, size, size);
  const data = img.data;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let nearest = 0;
      let minD = Infinity;
      let second = Infinity;
      for (let i = 0; i < pts.length; i++) {
        const dx = x - pts[i].x;
        const dy = y - pts[i].y;
        const d = dx * dx + dy * dy;
        if (d < minD) {
          second = minD;
          minD = d;
          nearest = i;
        } else if (d < second) {
          second = d;
        }
      }
      // Direction from cell centroid — the facet's tilt.
      const tiltX = (x - pts[nearest].x) / size;
      const tiltY = (y - pts[nearest].y) / size;

      // Edge softening: near cell boundaries the tilt attenuates so
      // facets meet each other in a valley rather than a ridge.
      const edgeCloseness = Math.max(
        0,
        1 - (second - minD) / (size * size * 0.02),
      );
      const attenuation = 1 - edgeCloseness * 0.6;

      const nx = Math.tanh(tiltX * 6) * 0.55 * attenuation;
      const ny = Math.tanh(tiltY * 6) * 0.55 * attenuation;

      const idx = (y * size + x) * 4;
      data[idx] = Math.round(128 + nx * 127);
      data[idx + 1] = Math.round(128 + ny * 127);
      data[idx + 2] = 255;
      data[idx + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.needsUpdate = true;
  return tex;
}


// -- The scene ------------------------------------------------------------

/**
 * Build the scene into a container. Returns a small API so React can
 * push parameter updates, take screenshots, and dispose cleanly.
 */
export function createLabScene({ container, initialParams }) {
  const width = container.clientWidth || window.innerWidth;
  const height = container.clientHeight || window.innerHeight;

  const scene = new THREE.Scene();
  // Darkness is never black. This is a very dark blue — inhabited,
  // not absent.
  scene.background = new THREE.Color(0x080810);

  const camera = new THREE.PerspectiveCamera(38, width / height, 0.01, 100);
  camera.position.set(0, 0, initialParams.cameraDistance);
  camera.lookAt(0, 0, -1);

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    preserveDrawingBuffer: true, // needed for screenshot
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  container.appendChild(renderer.domElement);

  // A subtle environment so refraction has something to refract.
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envScene = new THREE.Scene();
  envScene.background = new THREE.Color(0x0a0a14);
  const envTex = pmrem.fromScene(envScene, 0.04).texture;
  scene.environment = envTex;

  // Very faint hemisphere fill so darkness never renders as absence.
  const hemi = new THREE.HemisphereLight(0x1a1a24, 0x000000, 0.08);
  scene.add(hemi);

  // Facet normal map.
  let facetNormalMap = generateFacetNormalMap(initialParams.facetDensity);
  facetNormalMap.repeat.set(
    initialParams.facetScale,
    initialParams.facetScale,
  );

  // The material — the frost.
  const material = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    roughness: initialParams.surfaceRoughness,
    metalness: 0.0,
    transmission: initialParams.translucency,
    thickness: initialParams.surfaceDepth,
    ior: initialParams.refractionStrength,
    attenuationColor: new THREE.Color(0xffffff),
    attenuationDistance: 1 / (initialParams.subsurfaceScattering + 0.01),
    normalMap: facetNormalMap,
    normalScale: new THREE.Vector2(
      initialParams.facetVisibility * initialParams.facetTransparency,
      initialParams.facetVisibility * initialParams.facetTransparency,
    ),
    side: THREE.DoubleSide,
    opacity: initialParams.opacity,
    transparent: initialParams.opacity < 1,
    clearcoat: 0.15,
    clearcoatRoughness: 0.9,
  });

  // A slab of frost. High tessellation so normalMap deforms cleanly.
  const slabGeo = new THREE.BoxGeometry(4, 3, 0.2, 240, 180, 1);
  const slab = new THREE.Mesh(slabGeo, material);
  scene.add(slab);

  // The beacon — a point light behind the material.
  const pointLight = new THREE.PointLight(
    new THREE.Color(initialParams.lightColor),
    initialParams.lightIntensity,
    0, // no distance cutoff — light travels
    1.6,
  );
  pointLight.position.set(0, 0, -initialParams.lightDistance);
  scene.add(pointLight);

  // A small emissive sphere at the light position. It's what the
  // audience is actually seeing through the frost.
  const sourceGeo = new THREE.SphereGeometry(1, 32, 32);
  const sourceMat = new THREE.MeshBasicMaterial({
    color: new THREE.Color(initialParams.lightColor),
    toneMapped: false,
  });
  const lightSource = new THREE.Mesh(sourceGeo, sourceMat);
  lightSource.scale.setScalar(Math.max(0.001, initialParams.lightRadius));
  lightSource.position.copy(pointLight.position);
  scene.add(lightSource);

  // Track current params. React pushes new ones via setParams.
  let currentParams = { ...initialParams };
  let lastFacetDensity = initialParams.facetDensity;
  let lastFacetScale = initialParams.facetScale;

  function applyParams(p) {
    material.roughness = THREE.MathUtils.lerp(
      p.surfaceRoughness,
      p.surfaceRoughness * (1 + p.internalDiffusion * 0.2),
      p.internalDiffusion,
    );
    material.transmission = p.translucency;
    material.thickness = p.surfaceDepth;
    material.ior = p.refractionStrength;
    material.opacity = p.opacity;
    material.transparent = p.opacity < 1;
    material.attenuationDistance = 1 / (p.subsurfaceScattering + 0.01);

    const nScale = p.facetVisibility * p.facetTransparency;
    material.normalScale.set(nScale, nScale);

    if (p.facetDensity !== lastFacetDensity) {
      lastFacetDensity = p.facetDensity;
      const newMap = generateFacetNormalMap(p.facetDensity);
      newMap.repeat.set(p.facetScale, p.facetScale);
      material.normalMap = newMap;
      material.needsUpdate = true;
      facetNormalMap.dispose();
      facetNormalMap = newMap;
      lastFacetScale = p.facetScale;
    } else if (p.facetScale !== lastFacetScale) {
      facetNormalMap.repeat.set(p.facetScale, p.facetScale);
      facetNormalMap.needsUpdate = true;
      lastFacetScale = p.facetScale;
    }

    pointLight.color.set(p.lightColor);
    pointLight.position.set(0, 0, -p.lightDistance);
    lightSource.position.copy(pointLight.position);
    lightSource.scale.setScalar(Math.max(0.001, p.lightRadius));
    sourceMat.color.set(p.lightColor);
  }

  applyParams(currentParams);

  // Animation loop.
  const clock = new THREE.Clock();
  let running = true;
  const _color = new THREE.Color();

  function loop() {
    if (!running) return;
    requestAnimationFrame(loop);
    const t = clock.getElapsedTime();
    const p = currentParams;

    // Breathing pulse — never crosses zero (light is always alive).
    const pulse =
      1 + Math.sin(t * p.pulseFrequency * Math.PI * 2) * p.pulseAmplitude;
    pointLight.intensity = p.lightIntensity * pulse;

    // Match the source-sphere emissive brightness to the light pulse
    // so the visible source appears to breathe rather than blink.
    _color.set(p.lightColor).multiplyScalar(pulse);
    sourceMat.color.copy(_color);

    // Camera drift — two slightly out-of-phase sines, so the motion
    // never falls into a visible loop.
    const cx = Math.sin(t * p.cameraDriftSpeed) * p.cameraDriftAmplitude;
    const cy =
      Math.cos(t * p.cameraDriftSpeed * 0.73) * p.cameraDriftAmplitude;
    const cz =
      p.cameraDistance +
      Math.sin(t * p.cameraDriftSpeed * 0.41) *
        p.cameraDriftAmplitude *
        0.5;
    camera.position.set(cx, cy, cz);
    camera.lookAt(0, 0, -1);

    renderer.render(scene, camera);
  }
  loop();

  // Handle window resize.
  function onResize() {
    const w = container.clientWidth || window.innerWidth;
    const h = container.clientHeight || window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }
  window.addEventListener("resize", onResize);

  // Screenshot at up to 2x resolution, with a sidecar metadata JSON.
  function screenshot({ presetName = "unsaved", scale = 2 } = {}) {
    const oldSize = renderer.getSize(new THREE.Vector2());
    const w = Math.round(oldSize.x * scale);
    const h = Math.round(oldSize.y * scale);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.render(scene, camera);

    const dataUrl = renderer.domElement.toDataURL("image/png");

    // Restore.
    renderer.setSize(oldSize.x, oldSize.y, false);
    camera.aspect = oldSize.x / oldSize.y;
    camera.updateProjectionMatrix();

    const timestamp = new Date().toISOString();
    const meta = {
      timestamp,
      preset: presetName,
      cameraPosition: camera.position.toArray(),
      cameraOrientation: [camera.rotation.x, camera.rotation.y, camera.rotation.z],
      params: { ...currentParams },
    };

    const stamp = timestamp.replace(/[:.]/g, "-");
    const pngLink = document.createElement("a");
    pngLink.href = dataUrl;
    pngLink.download = `frame001_${stamp}.png`;
    document.body.appendChild(pngLink);
    pngLink.click();
    document.body.removeChild(pngLink);

    const blob = new Blob([JSON.stringify(meta, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const jsonLink = document.createElement("a");
    jsonLink.href = url;
    jsonLink.download = `frame001_${stamp}.json`;
    document.body.appendChild(jsonLink);
    jsonLink.click();
    document.body.removeChild(jsonLink);
    URL.revokeObjectURL(url);
  }

  function dispose() {
    running = false;
    window.removeEventListener("resize", onResize);
    if (renderer.domElement.parentNode === container) {
      container.removeChild(renderer.domElement);
    }
    slabGeo.dispose();
    material.dispose();
    sourceGeo.dispose();
    sourceMat.dispose();
    facetNormalMap.dispose();
    envTex.dispose();
    pmrem.dispose();
    renderer.dispose();
  }

  return {
    setParams(p) {
      currentParams = { ...p };
      applyParams(currentParams);
    },
    screenshot,
    dispose,
    getCameraState() {
      return {
        position: camera.position.toArray(),
        rotation: [camera.rotation.x, camera.rotation.y, camera.rotation.z],
      };
    },
  };
}
